import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileText, Lock } from 'lucide-react';

import { partnerAccountAPI } from '../lib/partnerAccountApi';
import { messageErreur } from '../lib/erreurs';
import { formatDate, formatMontant } from '../lib/formats';
import { libelle, nomLisible } from '../lib/libelles';
import { useAuth } from '../context/AuthContext';
import { usePaginationServeur } from '../hooks/usePagination';
import { useEstMobile } from '../hooks/useEstMobile';
import { useRafraichirSur } from '../hooks/useRafraichirSur';
import { PrintMenu } from '../components/business/PrintMenu';
import DetailEcriture from '../components/finance/DetailEcriture';
import NouvelleOperationCompte from '../components/partnerAccount/NouvelleOperationCompte';
import ReleveCompteDialog from '../components/partnerAccount/ReleveCompteDialog';
import { CartesCompte, TableauCompte } from '../components/partnerAccount/TableauCompte';
import { routeOrigine } from '../components/partnerAccount/origine';
import { Button } from '../components/ui/button';
import { ChiffresCles } from '../components/ui/chiffres-cles';
import { EmptyState } from '../components/ui/empty-state';
import { LoadingState } from '../components/ui/loading-state';
import { BarrePagination } from '../components/ui/pagination-bar';
import { PageHeader } from '../components/ui/page-header';
import { StatutBadge } from '../components/ui/StatutBadge';
import { toast } from '../components/ui/sonner';

const CADRE = 'mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8';

const TONS_SITUATION = { DOIT: 'attention', DU: 'neutre', SOLDE: 'positif' };

/**
 * Compte d'une société partenaire: ce que PKS lui a avancé, ce qu'elle a réglé
 * (décomptes, états de paiement, loyers, versements) et ce qu'elle doit encore.
 *
 * Le compte ne se saisit pas ici: il lit le journal de la finance. « Nouvelle
 * opération » renvoie vers les écrans existants, « Corriger » vers l'écran qui
 * sait annuler proprement la pièce.
 */
const CompteSocietePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canSeeMoney, isAdminSociete, isSuperAdmin } = useAuth();
  const estAdmin = Boolean(isAdminSociete || isSuperAdmin);
  const mobile = useEstMobile();

  const [compte, setCompte] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [dejaCharge, setDejaCharge] = useState(false);
  const [erreur, setErreur] = useState(false);
  const [ecriture, setEcriture] = useState(null);
  const [releveOuvert, setReleveOuvert] = useState(false);
  const rang = useRef(0);

  const pagination = usePaginationServeur(compte?.total ?? 0, { taillePrefere: 50 });
  const { page, limit } = pagination;

  const charger = useCallback(
    async ({ silencieux = false } = {}) => {
      const numero = ++rang.current;
      if (!silencieux) setChargement(true);
      try {
        const donnees = await partnerAccountAPI.compte(id, { page, page_size: limit });
        if (numero !== rang.current) return;
        setCompte(donnees);
        setErreur(false);
      } catch (err) {
        if (numero !== rang.current) return;
        console.error('Failed to load partner account:', err);
        setErreur(true);
        if (!silencieux) toast.error(messageErreur(err, 'Impossible de charger le compte du partenaire'));
      } finally {
        if (numero === rang.current) {
          setChargement(false);
          setDejaCharge(true);
        }
      }
    },
    [id, page, limit],
  );

  useEffect(() => {
    if (canSeeMoney) charger();
  }, [charger, canSeeMoney]);

  useRafraichirSur(['finance', 'lots', 'locations'], charger, { actif: Boolean(canSeeMoney) });

  if (!canSeeMoney) {
    return (
      <div className={CADRE}>
        <EmptyState variant="error" icon={Lock} title="Ces montants sont réservés à la direction" />
      </div>
    );
  }

  if (chargement && !dejaCharge) {
    return (
      <div className={CADRE} data-testid="compte-chargement">
        <LoadingState shape="table" rows={8} columns={7} label="Chargement du compte" />
      </div>
    );
  }

  if (!compte) {
    return (
      <div className={CADRE}>
        <EmptyState
          variant="error"
          title={erreur ? 'Le compte est indisponible' : 'Société introuvable'}
          actionLabel="Retour aux sociétés"
          onAction={() => navigate('/companies')}
        />
      </div>
    );
  }

  const devise = compte.currency || 'XOF';
  const resume = compte.summary || {};
  const lignes = compte.items || [];
  const nom = nomLisible(compte.company?.name, 'Société');
  const reste = Number(resume.balance) || 0;
  const situation = resume.situation || 'SOLDE';

  const ouvrirOrigine = (ligne) => {
    const route = routeOrigine(ligne.origin);
    if (route) navigate(route);
    else setEcriture(ligne.id);
  };
  // Une ligne que l'écran d'origine sait annuler proprement y est renvoyée;
  // seules les écritures directes se contre-passent ici, avec un motif.
  const corriger = (ligne) => {
    const route = routeOrigine(ligne.origin);
    if (ligne.can_reverse_directly || !route) setEcriture(ligne.id);
    else navigate(route);
  };

  const feuille = partnerAccountAPI.feuille(id);

  return (
    <div className={CADRE} data-testid="compte-societe">
      <PageHeader
        backTo="/companies"
        backLabel="Sociétés"
        eyebrow="Compte partenaire"
        title={nom}
        meta={<StatutBadge domaine="situation_compte" code={situation} data-testid="compte-situation" />}
        stats={[
          { label: 'Opérations', valeur: Number(resume.operations_count) || 0, testid: 'compte-stat-operations' },
          {
            label: 'Dernière opération',
            valeur: resume.last_operation_date ? formatDate(resume.last_operation_date) : '—',
            testid: 'compte-stat-derniere',
          },
        ]}
        actions={
          <>
            <NouvelleOperationCompte societeId={id} onEnregistre={() => charger({ silencieux: true })} />
            <PrintMenu
              label="Imprimer"
              items={[
                {
                  label: 'Compte',
                  url: feuille.url,
                  params: feuille.params,
                  filename: `compte-${nom}`,
                  formats: ['pdf', 'xlsx', 'csv'],
                },
              ]}
            />
            <Button type="button" variant="outline" size="sm" onClick={() => setReleveOuvert(true)} data-testid="compte-releve">
              <FileText className="h-4 w-4" aria-hidden="true" />
              Relevé de compte
            </Button>
          </>
        }
      />

      <ChiffresCles
        label="Le compte en un coup d’œil"
        chiffres={[
          {
            cle: 'reste',
            titre: 'Reste dû',
            valeur: formatMontant(reste, devise),
            negatif: reste < 0,
            etat: libelle('situation_compte', situation),
            ton: TONS_SITUATION[situation] || 'neutre',
            aide: 'Avances versées moins règlements. Positif: le partenaire doit encore cette somme à PKS. Négatif: PKS lui doit.',
          },
          {
            cle: 'debit',
            titre: 'Avances versées / Débit',
            valeur: formatMontant(resume.debit, devise),
            aide: 'Ce que PKS a avancé au partenaire, plus les avoirs reçus.',
          },
          {
            cle: 'credit',
            titre: 'Règlements / Crédit',
            valeur: formatMontant(resume.credit, devise),
            aide: 'Ce que le partenaire a réglé ou facturé: décomptes, états de paiement, fret, remboursements.',
          },
        ]}
      />

      {lignes.length === 0 ? (
        <EmptyState
          variant="empty"
          icon={FileText}
          title="Aucune opération sur ce compte"
          aide="Les acomptes, décomptes, états de paiement et le fret de cette société apparaîtront ici."
        />
      ) : mobile ? (
        <CartesCompte
          lignes={lignes}
          devise={devise}
          estAdmin={estAdmin}
          onOuvrir={ouvrirOrigine}
          onCorriger={corriger}
          onVoirAnnulation={setEcriture}
        />
      ) : (
        <div className="rounded-2xl border border-border/70 bg-card shadow-sm">
          <TableauCompte
            lignes={lignes}
            devise={devise}
            estAdmin={estAdmin}
            onOuvrir={ouvrirOrigine}
            onCorriger={corriger}
            onVoirAnnulation={setEcriture}
          />
        </div>
      )}

      {lignes.length > 0 ? <BarrePagination pagination={pagination} nom="opérations" /> : null}

      <DetailEcriture
        operationId={ecriture}
        onFermer={() => setEcriture(null)}
        onModifie={() => charger({ silencieux: true })}
        peutSaisir={estAdmin}
      />
      <ReleveCompteDialog
        ouvert={releveOuvert}
        onOpenChange={setReleveOuvert}
        societeId={id}
        premiereOperation={resume.first_operation_date}
      />
    </div>
  );
};

export default CompteSocietePage;
